// settings/pages/CurrenciesPage.tsx
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { SettingsModal } from '../components/SettingsModal';
import * as api from '../services/settingsApi';
import {
    DollarSign, Plus, Save, ArrowLeft, Pencil, Trash2, Star,
    MoreVertical, TrendingUp
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';

interface Currency {
    id: string;
    code: string;
    name: string;
    symbol: string;
    exchangeRate: number;
    decimalPlaces: number;
    isBaseCurrency: boolean;
    isActive: boolean;
}

const CURRENCY_FLAGS: Record<string, string> = {
    NGN: '🇳🇬', USD: '🇺🇸', EUR: '🇪🇺', GBP: '🇬🇧', GHS: '🇬🇭',
    KES: '🇰🇪', ZAR: '🇿🇦', JPY: '🇯🇵', CNY: '🇨🇳', INR: '🇮🇳',
};

const CURRENCY_COLORS: Record<string, string> = {
    NGN: 'from-emerald-500 to-green-600', USD: 'from-blue-500 to-indigo-600',
    EUR: 'from-indigo-500 to-purple-600', GBP: 'from-purple-500 to-violet-600',
    GHS: 'from-amber-500 to-orange-600', KES: 'from-rose-500 to-pink-600',
    ZAR: 'from-teal-500 to-cyan-600',
};

export function CurrenciesPage() {
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);
    const [showForm, setShowForm] = useState(false);
    const [editingCurrency, setEditingCurrency] = useState<Currency | null>(null);
    const [form, setForm] = useState({ code: '', name: '', symbol: '', exchangeRate: '1', decimalPlaces: '2', isBaseCurrency: false });
    const [openMenuId, setOpenMenuId] = useState<string | null>(null);

    const { data: currencies, isLoading } = useQuery<Currency[]>({
        queryKey: ['currencies', user?.tenantId],
        queryFn: () => api.getCurrencies(user!.tenantId),
        enabled: !!user?.tenantId,
    });

    const saveMutation = useMutation({
        mutationFn: (payload: any) => editingCurrency
            ? api.updateCurrency(user!.tenantId, editingCurrency.id, payload)
            : api.createCurrency(user!.tenantId, payload),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['currencies'] });
            addToast({ type: 'success', title: editingCurrency ? 'Currency updated' : 'Currency created' });
            handleCloseForm();
        },
        onError: (err: any) => addToast({ type: 'error', title: 'Failed', message: err?.response?.data?.error?.message }),
    });

    const deleteMutation = useMutation({
        mutationFn: (currencyId: string) => api.deleteCurrency(user!.tenantId, currencyId),
        onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['currencies'] }); addToast({ type: 'success', title: 'Deleted' }); },
    });

    const setBaseMutation = useMutation({
        mutationFn: (currencyId: string) => api.setBaseCurrency(user!.tenantId, currencyId),
        onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['currencies'] }); addToast({ type: 'success', title: 'Base currency updated' }); },
    });

    const handleEdit = (currency: Currency) => {
        setEditingCurrency(currency);
        setForm({ code: currency.code, name: currency.name, symbol: currency.symbol, exchangeRate: String(currency.exchangeRate), decimalPlaces: String(currency.decimalPlaces), isBaseCurrency: currency.isBaseCurrency });
        setShowForm(true);
        setOpenMenuId(null);
    };

    const handleCloseForm = () => {
        setShowForm(false);
        setEditingCurrency(null);
        setForm({ code: '', name: '', symbol: '', exchangeRate: '1', decimalPlaces: '2', isBaseCurrency: false });
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                    className="w-14 h-14 rounded-2xl border-4 border-emerald-200 border-t-emerald-600" />
            </div>
        );
    }

    const baseCurrency = (currencies || []).find(c => c.isBaseCurrency);
    const activeCurrencies = (currencies || []).filter(c => c.isActive).length;

    return (
        <div className="min-h-screen">
            <div className="max-w-[1600px] mx-auto p-4 lg:p-6 space-y-5">

                <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}>
                    <button onClick={() => navigate('/settings')}
                        className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-emerald-600 transition-colors mb-3 group">
                        <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
                        Back to Settings
                    </button>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div>
                            <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 mt-1">
                                💱 Currencies
                            </h1>
                            <p className="text-slate-500 text-sm mt-1">
                                {currencies?.length || 0} currenc{(currencies?.length || 0) !== 1 ? 'ies' : 'y'} · {activeCurrencies} active · Base: {baseCurrency?.code || 'Not set'}
                            </p>
                        </div>
                        <Button size="md" leftIcon={<Plus size={16} />} onClick={() => { handleCloseForm(); setShowForm(true); }}>
                            Add Currency
                        </Button>
                    </div>
                </motion.div>

                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                        { label: 'Total Currencies', value: currencies?.length || 0, gradient: 'from-green-500 to-emerald-600' },
                        { label: 'Active', value: activeCurrencies, gradient: 'from-emerald-500 to-teal-600' },
                        { label: 'Base Currency', value: baseCurrency?.code || '—', gradient: 'from-amber-500 to-orange-600' },
                        { label: 'Exchange Rates', value: (currencies || []).filter(c => c.exchangeRate !== 1).length, gradient: 'from-blue-500 to-indigo-600' },
                    ].map((stat, i) => (
                        <motion.div key={stat.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                            className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
                            <div className={`p-2 rounded-lg bg-gradient-to-br ${stat.gradient} shadow-md w-fit mb-3`}>
                                <DollarSign className="w-4 h-4 text-white" />
                            </div>
                            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">{stat.label}</p>
                            <p className="text-xl font-bold text-slate-900 mt-0.5">{stat.value}</p>
                        </motion.div>
                    ))}
                </div>

                {/* Currencies Grid */}
                {!currencies?.length ? (
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                        className="text-center py-20 bg-white rounded-3xl border border-slate-200 ">
                        <div className="w-20 h-20 bg-slate-100 rounded-3xl flex items-center justify-center mx-auto mb-6">
                            <DollarSign className="w-10 h-10 text-slate-300" />
                        </div>
                        <h3 className="text-xl font-bold text-slate-700 ">No currencies configured</h3>
                        <p className="text-slate-400 mt-2 max-w-md mx-auto">Add currencies for multi-currency support with exchange rates.</p>
                        <button onClick={() => setShowForm(true)}
                            className="mt-6 px-6 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl hover:from-emerald-700 hover:to-teal-700 transition-all font-semibold shadow-lg shadow-emerald-500/25 inline-flex items-center gap-2">
                            <Plus className="w-5 h-5" /> Add First Currency
                        </button>
                    </motion.div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {(currencies || []).map((currency, i) => (
                            <motion.div key={currency.id}
                                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                                whileHover={{ y: -3 }}
                                className={clsx(
                                    'group relative bg-white rounded-2xl border-2 transition-all duration-300 overflow-hidden',
                                    currency.isBaseCurrency ? 'border-emerald-400 shadow-lg shadow-emerald-500/10' : 'border-slate-200 shadow-sm hover:shadow-lg'
                                )}>
                                {currency.isBaseCurrency && <div className="h-1 bg-gradient-to-r from-emerald-500 to-teal-600" />}
                                <div className="p-5">
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="flex items-center gap-3">
                                            <div className={clsx(
                                                'w-12 h-12 rounded-xl flex items-center justify-center text-2xl shadow-md',
                                                currency.isBaseCurrency
                                                    ? `bg-gradient-to-br ${CURRENCY_COLORS[currency.code] || 'from-slate-500 to-slate-700'}`
                                                    : 'bg-slate-100 '
                                            )}>
                                                {CURRENCY_FLAGS[currency.code] || currency.symbol}
                                            </div>
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <h3 className="font-semibold text-slate-900 ">{currency.code}</h3>
                                                    {currency.isBaseCurrency && <Star size={12} className="text-amber-500 fill-amber-500" />}
                                                </div>
                                                <p className="text-sm text-slate-500">{currency.name}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <Badge variant={currency.isActive ? 'success' : 'danger'} size="sm">
                                                {currency.isActive ? 'Active' : 'Inactive'}
                                            </Badge>
                                            <div className="relative">
                                                <button onClick={() => setOpenMenuId(openMenuId === currency.id ? null : currency.id)}
                                                    className="p-1.5 rounded-lg hover:bg-slate-100 :bg-slate-700 text-slate-400">
                                                    <MoreVertical size={14} />
                                                </button>
                                                <AnimatePresence>
                                                    {openMenuId === currency.id && (
                                                        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }}
                                                            className="absolute right-0 top-full mt-1 bg-white border border-slate-200 rounded-xl shadow-xl z-10 py-1 w-40">
                                                            <button onClick={() => handleEdit(currency)}
                                                                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50"><Pencil size={13} /> Edit</button>
                                                            {!currency.isBaseCurrency && (
                                                                <button onClick={() => { setBaseMutation.mutate(currency.id); setOpenMenuId(null); }}
                                                                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50"><Star size={13} /> Set Base</button>
                                                            )}
                                                            {!currency.isBaseCurrency && (
                                                                <button onClick={() => { if (confirm('Delete?')) deleteMutation.mutate(currency.id); }}
                                                                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-rose-500 hover:bg-rose-50"><Trash2 size={13} /> Delete</button>
                                                            )}
                                                        </motion.div>
                                                    )}
                                                </AnimatePresence>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="space-y-2 p-3 bg-slate-50 rounded-xl">
                                        <div className="flex justify-between text-sm">
                                            <span className="text-slate-500">Symbol</span>
                                            <span className="font-semibold text-slate-900 ">{currency.symbol}</span>
                                        </div>
                                        <div className="flex justify-between text-sm">
                                            <span className="text-slate-500">Rate</span>
                                            <span className="font-semibold text-slate-900 ">{currency.exchangeRate.toFixed(4)}</span>
                                        </div>
                                        <div className="flex justify-between text-sm">
                                            <span className="text-slate-500">Decimals</span>
                                            <span className="font-semibold text-slate-900 ">{currency.decimalPlaces}</span>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                )}

                {/* Add/Edit Modal */}
                <SettingsModal
                    open={showForm}
                    onClose={handleCloseForm}
                    title={editingCurrency ? 'Edit Currency' : 'Add Currency'}
                    subtitle="Configure currency and exchange rate"
                    icon={DollarSign}
                    footer={
                        <>
                            <Button variant="secondary" onClick={handleCloseForm}>Cancel</Button>
                            <Button leftIcon={<Save size={16} />} onClick={() => saveMutation.mutate({ ...form, exchangeRate: parseFloat(form.exchangeRate) || 1, decimalPlaces: parseInt(form.decimalPlaces) || 2 })}
                                isLoading={saveMutation.isPending} disabled={!form.code || !form.name || !form.symbol}>
                                {editingCurrency ? 'Update' : 'Save Currency'}
                            </Button>
                        </>
                    }
                >
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                            <Input label="Currency Code *" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} maxLength={3} placeholder="USD" required />
                            <Input label="Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="US Dollar" required />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <Input label="Symbol *" value={form.symbol} onChange={(e) => setForm({ ...form, symbol: e.target.value })} placeholder="$" required />
                            <Input label="Exchange Rate" type="number" step="0.0001" value={form.exchangeRate} onChange={(e) => setForm({ ...form, exchangeRate: e.target.value })} />
                        </div>
                        <Input label="Decimal Places" type="number" value={form.decimalPlaces} onChange={(e) => setForm({ ...form, decimalPlaces: e.target.value })} />
                        <label className="flex items-center gap-2 cursor-pointer p-3 bg-slate-50 rounded-xl">
                            <input type="checkbox" checked={form.isBaseCurrency} onChange={(e) => setForm({ ...form, isBaseCurrency: e.target.checked })}
                                className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
                            <div>
                                <span className="text-sm font-medium text-slate-700 ">Set as Base Currency</span>
                                <p className="text-xs text-slate-400">This will be the primary currency for your business</p>
                            </div>
                        </label>
                    </div>
                </SettingsModal>
            </div>
        </div>
    );
}